"use client";

import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { format, isValid } from "date-fns";
import type { BookingCurrency } from "@/types";
import { PAGE_COPY } from "@/constants/pages";
import { formatServicePrice } from "@/utils/services";

type ReceiptService = {
  name: string;
  sessionLength: number;
  price: number;
};

type ReceiptPreviewProps = {
  name: string | null;
  email: string | null;
  phone: string | null;
  service: ReceiptService | null;
  currency?: BookingCurrency;
  date: Date | null;
  slot: { start: string; end: string } | null;
  hideContactDetails?: boolean;
};

export const ReceiptPreview = ({
  name,
  email,
  phone,
  service,
  currency = "usd",
  date,
  slot,
  hideContactDetails = false,
}: ReceiptPreviewProps) => {
  const receiptCopy = PAGE_COPY.booking.receipt;
  const fallbackValue = receiptCopy.fallback;
  const formattedDate =
    date && isValid(date) ? format(date, receiptCopy.dateFormat) : fallbackValue;
  const contactFallbackValue = hideContactDetails
    ? receiptCopy.protectedContactFallback
    : fallbackValue;

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" mb={2}>
        {receiptCopy.heading}
      </Typography>

      <Stack spacing={1.5}>
        <Box>
          <Typography variant="caption">{receiptCopy.labels.name}</Typography>
          <Typography>{name || contactFallbackValue}</Typography>
        </Box>

        <Box>
          <Typography variant="caption">{receiptCopy.labels.phone}</Typography>
          <Typography>{phone || contactFallbackValue}</Typography>
        </Box>

        <Box>
          <Typography variant="caption">{receiptCopy.labels.email}</Typography>
          <Typography>{email || contactFallbackValue}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="caption">{receiptCopy.labels.service}</Typography>
          <Typography>{service?.name || fallbackValue}</Typography>
        </Box>

        <Box>
          <Typography variant="caption">{receiptCopy.labels.sessionLength}</Typography>
          <Typography>
            {service?.sessionLength} {receiptCopy.minutesSuffix}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption">{receiptCopy.labels.date}</Typography>
          <Typography>{formattedDate}</Typography>
        </Box>

        <Box>
          <Typography variant="caption">{receiptCopy.labels.time}</Typography>
          <Typography>{slot ? `${slot.start} - ${slot.end}` : fallbackValue}</Typography>
        </Box>

        <Box>
          <Typography variant="caption">{receiptCopy.labels.pricePerSession}</Typography>
          <Typography>
            {typeof service?.price === "number"
              ? formatServicePrice(service.price, currency)
              : fallbackValue}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};
